import { z } from 'zod';
// Zod schemas for validation
/**
 * Schema for avatar cache status
 */
export const AvatarCacheStatusSchema = z.enum(['pending', 'cached', 'failed']);
/**
 * Schema for employee data with cached avatar support
 */
export const EmployeeDataSchema = z.object({
    // Basic employee information
    id: z.union([z.string(), z.number()]).optional(),
    name: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string().email().optional(),
    status: z.string().optional(),
    role_name: z.string().optional(),
    position: z.string().optional(),
    team_name: z.string().optional(),
    department: z.string().optional(),
    manager_level: z.string().optional(),
    is_admin: z.union([z.string(), z.boolean()]).optional(),
    hire_date: z.union([
        z.object({ value: z.string() }),
        z.date()
    ]).optional(),
    employee_start: z.union([
        z.object({ value: z.string() }),
        z.date(),
        z.null()
    ]).optional(),
    employee_end: z.union([
        z.object({ value: z.string() }),
        z.date(),
        z.null()
    ]).optional(),
    bamboo_id: z.string().optional(),
    // Avatar fields - backward compatible
    avatar: z.string().url().optional(),
    // New cached avatar fields
    cached_avatar_url: z.string().url().optional(),
    cached_thumbnail_url: z.string().url().optional(),
    avatar_cache_timestamp: z.date().optional(),
    avatar_cache_status: AvatarCacheStatusSchema.optional(),
    // Additional HRIS fields
    team_id: z.union([z.string(), z.number(), z.null()]).optional(),
    division_id: z.union([z.string(), z.number(), z.null()]).optional(),
    role_id: z.union([z.string(), z.number()]).optional(),
    m1_id: z.union([z.string(), z.number(), z.null()]).optional(),
    m2_id: z.union([z.string(), z.number(), z.null()]).optional(),
    manager_user_id: z.union([z.string(), z.number(), z.null()]).optional(),
});
/**
 * Schema for system user permissions
 */
export const SystemUserPermissionsSchema = z.object({
    canAdmin: z.boolean(),
    canSetup: z.boolean(),
    canReview: z.boolean(),
    canCollaborate: z.boolean(),
    canViewSensitiveFinanceData: z.boolean().default(false),
});
/**
 * Schema for system user
 */
export const SystemUserSchema = z.object({
    id: z.string(),
    employee_id: z.number(),
    firebase_uid: z.string().optional(),
    email: z.string().email(),
    permissions: SystemUserPermissionsSchema,
    role: z.string().optional(),
    groups: z.array(z.string()).optional(),
    isSuperAdmin: z.boolean().optional(),
    slack_user_id: z.string().optional(),
    asana_user_gid: z.string().optional(),
    assigned_workflows: z.array(z.number()),
    program_memberships: z.array(z.string()),
    starred_workflows: z.array(z.string()).optional(),
    starred_users: z.array(z.string()).optional(),
    starred_teams: z.array(z.string()).optional(),
    employee_data: EmployeeDataSchema.optional(),
    crm_saved_view: z.object({ filters: z.array(z.any()), savedAt: z.date() }).optional(),
    crm_saved_view_people: z.object({ filters: z.array(z.any()), savedAt: z.date() }).optional(),
    data_collection_complete: z.boolean().optional(),
    data_collection_completed_at: z.date().optional(),
    association_status: z.enum(['pending', 'associated', 'manual', 'new_user_pending']),
    created_at: z.date(),
    updated_at: z.date(),
    created_by: z.string().optional(),
    updated_by: z.string().optional(),
});
/**
 * Schema for system user profile with cached avatar support
 */
export const SystemUserProfileSchema = z.object({
    uid: z.string(),
    employee_id: z.number(),
    email: z.string().email(),
    name: z.string(),
    team_name: z.string(),
    role_name: z.string(),
    role: z.enum(['admin', 'manager', 'contributor']),
    lastActive: z.date(),
    // Avatar fields - backward compatible
    avatar: z.string().url().optional(),
    // New cached avatar fields
    cached_avatar_url: z.string().url().optional(),
    cached_thumbnail_url: z.string().url().optional(),
    avatar_cache_timestamp: z.date().optional(),
    avatar_cache_status: AvatarCacheStatusSchema.optional(),
});
/**
 * Schema for creating system user data
 */
export const CreateSystemUserDataSchema = z.object({
    employee_id: z.number(),
    firebase_uid: z.string().optional(),
    email: z.string().email(),
    permissions: SystemUserPermissionsSchema.partial().optional(),
    assigned_workflows: z.array(z.number()).optional().default([]),
    program_memberships: z.array(z.string()).optional().default([]),
    employee_data: EmployeeDataSchema.partial().optional(),
    association_status: z.enum(['pending', 'associated', 'manual', 'new_user_pending']).optional().default('pending'),
    created_by: z.string().optional(),
});
/**
 * Schema for updating system user data
 */
export const UpdateSystemUserDataSchema = z.object({
    firebase_uid: z.string().optional(),
    email: z.string().email().optional(),
    permissions: SystemUserPermissionsSchema.partial().optional(),
    assigned_workflows: z.array(z.number()).optional(),
    program_memberships: z.array(z.string()).optional(),
    employee_data: EmployeeDataSchema.partial().optional(),
    association_status: z.enum(['pending', 'associated', 'manual', 'new_user_pending']).optional(),
    data_collection_complete: z.boolean().optional(),
    data_collection_completed_at: z.date().optional(),
});
/**
 * Type guards for system user data
 */
export const isValidSystemUser = (data) => {
    return SystemUserSchema.safeParse(data).success;
};
export const isValidEmployeeData = (data) => {
    return EmployeeDataSchema.safeParse(data).success;
};
export const isValidSystemUserProfile = (data) => {
    return SystemUserProfileSchema.safeParse(data).success;
};
export const isValidAvatarCacheStatus = (status) => {
    return AvatarCacheStatusSchema.safeParse(status).success;
};
/**
 * Helper functions for avatar cache management
 */
export const avatarCacheHelpers = {
    /**
     * Helper function to convert Firestore Timestamp to Date
     */
    convertTimestamp: (timestamp) => {
        if (!timestamp)
            return undefined;
        // If it's already a Date object, return it
        if (timestamp instanceof Date)
            return timestamp;
        // If it's a Firestore Timestamp, convert it
        if (timestamp && typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
        }
        // If it's a timestamp object with seconds/nanoseconds
        if (timestamp && typeof timestamp.seconds === 'number') {
            return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
        }
        // Try to parse as date string
        if (typeof timestamp === 'string') {
            const parsed = new Date(timestamp);
            return isNaN(parsed.getTime()) ? undefined : parsed;
        }
        return undefined;
    },
    /**
     * Check if avatar cache is valid (not expired)
     */
    isCacheValid: (timestamp, maxAgeHours = 24) => {
        const convertedTimestamp = avatarCacheHelpers.convertTimestamp(timestamp);
        if (!convertedTimestamp)
            return false;
        const now = new Date();
        const ageHours = (now.getTime() - convertedTimestamp.getTime()) / (1000 * 60 * 60);
        return ageHours < maxAgeHours;
    },
    /**
     * Get the best available cached avatar URL (Firebase Storage only)
     */
    getBestAvatarUrl: (employeeData) => {
        if (!employeeData)
            return undefined;
        // Only return cached avatar if available and status is 'cached'
        if (employeeData.cached_avatar_url && employeeData.avatar_cache_status === 'cached') {
            return employeeData.cached_avatar_url;
        }
        // No fallback to original avatar - show initials instead
        return undefined;
    },
    /**
     * Get thumbnail URL if available
     */
    getThumbnailUrl: (employeeData) => {
        if (!employeeData)
            return undefined;
        // Return thumbnail if available and status is 'cached'
        if (employeeData.cached_thumbnail_url && employeeData.avatar_cache_status === 'cached') {
            return employeeData.cached_thumbnail_url;
        }
        return undefined;
    },
    /**
     * Check if avatar needs caching (no cached version or cache is stale)
     * Note: This is primarily for migration/admin purposes since we no longer rely on original avatars
     */
    needsCaching: (employeeData, maxAgeHours = 24) => {
        if (!employeeData?.avatar)
            return false; // No original avatar to cache
        // If no cached version exists
        if (!employeeData.cached_avatar_url || employeeData.avatar_cache_status !== 'cached') {
            return true;
        }
        // If cache is stale
        return !avatarCacheHelpers.isCacheValid(employeeData.avatar_cache_timestamp, maxAgeHours);
    }
};
